import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import { 
  PhotoFilters, 
  VillageGroup, 
  ImpactPhoto, 
  PaginationInfo, 
  ApiResponse, 
  UseImpactPhotosReturn 
} from '../types/photo';

const IMPACT_PHOTOS_ENDPOINT = '/console/asset_management/impact/list';

export const useImpactPhotos = (initialFilters: PhotoFilters): UseImpactPhotosReturn => {
  const [villages, setVillages] = useState<VillageGroup[]>([]);
  const [photos, setPhotos] = useState<ImpactPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page_number: 1,
    page_size: 10,
    has_next_page: false
  });

  const [currentFilters, setCurrentFilters] = useState<PhotoFilters>(initialFilters);
  const [hasMoreVillages, setHasMoreVillages] = useState(false);

  // Build query string from filters
  const buildQueryString = useCallback((filters: PhotoFilters): string => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    return params.toString();
  }, []);

  // Fetch photos from API
  const fetchPhotos = useCallback(async (filters: PhotoFilters, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const queryString = buildQueryString(filters);
      const url = `${IMPACT_PHOTOS_ENDPOINT}?${queryString}`;
      
      console.log('Fetching photos from:', url);
      
      const response = await apiClient.get<ApiResponse>(url);
      
      if (response.data) {
        const { data } = response.data;
        
        // Get all villages from API
        let allVillages = data.list || [];
        
        // Implement client-side pagination limits for villages
        const villagePageSize = filters.page_size || 10;
        
        let processedVillages: VillageGroup[];
        
        if (!append) {
          // For initial load, take only first villagePageSize villages
          processedVillages = allVillages.slice(0, villagePageSize);
        } else {
          // For load more, calculate which villages to show next
          // We need to get the next batch after the current villages count
          const currentVillageCount = villages.length;
          const nextBatch = allVillages.slice(currentVillageCount, currentVillageCount + villagePageSize);
          processedVillages = nextBatch;
        }
        
        // Use the API's has_next_page flag from each village object directly
        // No client-side overwriting — the API knows if more groups are available
        processedVillages = processedVillages.map(village => ({
          ...village,
          group_number: 1
        }));

        // Update villages - append if load more, replace if initial load
        setVillages(prev => {
          if (append) {
            // Append new villages, ensuring no duplicates
            const existingIds = new Set(prev.map(v => v.village_id));
            const newVillages = processedVillages.filter(v => !existingIds.has(v.village_id));
            return [...prev, ...newVillages];
          } else {
            return processedVillages;
          }
        });
        
        // Check if there are more villages available
        const totalVillagesFromApi = allVillages.length;
        const currentVillageCount = append ? villages.length + processedVillages.length : processedVillages.length;
        
        // Check if we have more villages available to load
        const hasMoreVillagesAvailable = totalVillagesFromApi > currentVillageCount;
        
        // Also check if the API indicates more pages
        const apiHasMore = data.has_next_page !== undefined ? data.has_next_page : false;
        
        setHasMoreVillages(hasMoreVillagesAvailable || apiHasMore);
        
        // Flatten all photos from processed villages (respecting pagination limits)
        const newPhotos = processedVillages.reduce<ImpactPhoto[]>((acc, village) => {
          return [...acc, ...village.assets];
        }, []);
        
        // Update photos - append if load more, replace if initial load
        setPhotos(prev => {
          if (append) {
            // Append new photos, ensuring no duplicates
            const existingIds = new Set(prev.map(p => p.image_id));
            const uniqueNewPhotos = newPhotos.filter(p => !existingIds.has(p.image_id));
            return [...prev, ...uniqueNewPhotos];
          } else {
            return newPhotos;
          }
        });
        
        // Update pagination info
        setPagination({
          page_number: data.page_number,
          page_size: data.page_size,
          has_next_page: data.has_next_page,
          total_pages: data.has_next_page ? undefined : data.page_number
        });

        console.log('Photos loaded successfully:', {
          totalVillagesFromApi: allVillages.length,
          processedVillages: processedVillages.length,
          totalPhotos: newPhotos.length,
          currentVillageCount,
          hasMoreVillages: hasMoreVillagesAvailable || apiHasMore,
          pagination: data,
          append,
          villagePageSize
        });
      }
    } catch (err) {
      console.error('Error fetching photos:', err);
      
      let errorMessage = 'Failed to load photos';
      
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.status) {
          errorMessage = `HTTP Error ${response.status}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      if (!append) {
        setVillages([]);
        setPhotos([]);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [buildQueryString]);

  // Refetch with current filters
  const refetch = useCallback(() => {
    fetchPhotos(currentFilters);
  }, [fetchPhotos, currentFilters]);

  // Update filters and refetch
  const updateFilters = useCallback((newFilters: PhotoFilters) => {
    console.log('Updating filters:', newFilters);
    setCurrentFilters(newFilters);
    fetchPhotos(newFilters);
  }, [fetchPhotos]);

  // Load more villages (next page)
  const loadMoreVillages = useCallback(() => {
    if (!hasMoreVillages || loadingMore) return;
    
    const nextPageFilters = {
      ...currentFilters,
      page_number: pagination.page_number + 1
    };
    
    fetchPhotos(nextPageFilters, true);
  }, [currentFilters, pagination.page_number, hasMoreVillages, loadingMore, fetchPhotos]);

  // Load more images within a specific village
  const loadMoreImagesForVillage = useCallback(async (villageId: string) => {
    const village = villages.find(v => v.village_id === villageId);
    if (!village || !village.has_next_page) return;

    try {
      setLoadingMore(true);

      const userGroupSize = currentFilters.group_size || 20;
      const currentImageCount = village.assets.length;
      const nextGroup = Math.ceil(currentImageCount / userGroupSize) + 1;

      console.log('Loading more images for village:', {
        villageId,
        userGroupSize,
        currentImageCount,
        nextGroup,
        villageName: village.village_name
      });

      const villageFilters = {
        ...currentFilters,
        village_id: villageId,
        page_number: 1,
        page_size: 1,
        group_number: nextGroup,
        group_size: userGroupSize
      };

      const queryString = buildQueryString(villageFilters);
      const url = `${IMPACT_PHOTOS_ENDPOINT}?${queryString}`;

      const response = await apiClient.get<ApiResponse>(url);

      if (response.data?.data?.list?.length > 0) {
        const updatedVillageData = response.data.data.list.find(v => v.village_id === villageId);

        if (updatedVillageData && updatedVillageData.assets.length > 0) {
          const newImages = updatedVillageData.assets;
          const hasMoreImages = updatedVillageData.has_next_page || false;

          setVillages(prev => {
            return prev.map(v => {
              if (v.village_id === villageId) {
                return {
                  ...v,
                  assets: [...v.assets, ...newImages],
                  has_next_page: hasMoreImages,
                  group_number: nextGroup
                };
              }
              return v;
            });
          });

          setPhotos(prev => {
            const existingIds = new Set(prev.map(p => p.image_id));
            const uniqueNewImages = newImages.filter(a => !existingIds.has(a.image_id));
            return [...prev, ...uniqueNewImages];
          });
        }
      }
    } catch (err) {
      console.error('Error loading more images for village:', err);

      let errorMessage = 'Failed to load more images for this village';

      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as any).response;
        if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.data?.error) {
          errorMessage = response.data.error;
        } else if (response?.status) {
          errorMessage = `HTTP Error ${response.status}: Failed to load more images`;
        }
      } else if (err instanceof Error) {
        errorMessage = `Load More Error: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoadingMore(false);
    }
  }, [villages, currentFilters, buildQueryString]);

  // Initial load
  useEffect(() => {
    fetchPhotos(currentFilters);
  }, []); // Only run once on mount

  return {
    photos,
    villages,
    loading,
    loadingMore,
    error,
    pagination,
    hasMoreVillages,
    refetch,
    updateFilters,
    loadMoreVillages,
    loadMoreImagesForVillage
  };
};